import React, { useState, useEffect } from "react";
import { API } from "aws-amplify";
import {
  Box,
  TextField,
  Button,
  CircularProgress
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import QuestionCard from "./QuestionCard";
import PracticePageLayout, {
  inputSx,
  primaryButtonSx
} from "./PracticePageLayout";

const Question = () => {
  const [jobPosition, setJobPosition] = useState("");
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [conversationText, setConversationText] = useState([]);
  const [blobURLs, setBlobURLs] = useState([]);
  const [audioKeys, setAudioKeys] = useState([]);
  const [responseTexts, setResponseTexts] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setCurrentQuestionIndex(0);
  }, [questions]);
  const handlePrevQuestion = () => {
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  const handleNextQuestion = () => {
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };
  const handleBlobURLChange = (index, blobURL) => {
    const newBlobURLs = [...blobURLs];
    newBlobURLs[index] = blobURL;
    setBlobURLs(newBlobURLs);
  };

  const handleAudioKeyChange = (index, audioKey) => {
    const newAudioKeys = [...audioKeys];
    newAudioKeys[index] = audioKey;
    setAudioKeys(newAudioKeys);
  };
  const handleConversationTextChange = newItems => {
    setConversationText([...conversationText, ...newItems]);
    console.log([...conversationText, ...newItems]);
  };

  const handleResponseTextChange = (index, responseText) => {
    const newResponseTexts = [...responseTexts];
    newResponseTexts[index] = responseText;
    setResponseTexts(newResponseTexts);
  };

  const handleFeedbackChange = (index, feedback) => {
    const newFeedbacks = [...feedbacks];
    newFeedbacks[index] = feedback;
    setFeedbacks(newFeedbacks);
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    let fullQuery = `As an interviewer for a ${jobPosition} position, please generate ten questions an interviewee should expect in an interview. Please format your response as a JSON with an array of ten strings, with each string containing a question. For example:

    {
      "questions": [
        //List of questions for the specific job position
      ]
    }

    If there is nothing to say about a particular field, simply leave the array empty. Please make sure to use the exact field names provided (questions), and include double quotes around all strings. Do not include any additional text in your response
    `;
    const request = [{ role: "user", content: fullQuery }];
    try {
      await API.post("chatEndpoints", "/chat/completion", {
        body: request,
        headers: { "Content-Type": "application/json" }
      }).then(res => {
        const result = res.query;
        handleConversationTextChange(request, result);
        const json = JSON.parse(result.content);
        console.log(json);
        const newQuestions = json.questions.map(question => question);
        setQuestions(newQuestions);
        setBlobURLs(new Array(newQuestions.length).fill(""));
        setAudioKeys(new Array(newQuestions.length).fill(""));
        setResponseTexts(new Array(newQuestions.length).fill(""));
        setFeedbacks(new Array(newQuestions.length).fill(""));
        setLoading(false);
      });
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const questionCards = questions.map((question, index) => {
    return (
      <QuestionCard
        key={index}
        question={question}
        index={index}
        total={questions.length}
        conversation={conversationText}
        blobURL={blobURLs[index]}
        audioKey={audioKeys[index]}
        responseText={responseTexts[index]}
        feedback={feedbacks[index]}
        onBlobURLChange={blobURL => handleBlobURLChange(index, blobURL)}
        onAudioKeyChange={audioKey => handleAudioKeyChange(index, audioKey)}
        onResponseTextChange={responseText =>
          handleResponseTextChange(index, responseText)
        }
        onConversationTextChange={handleConversationTextChange}
        onFeedbackChange={feedback => handleFeedbackChange(index, feedback)}
        next={handleNextQuestion}
        prev={handlePrevQuestion}
      />
    );
  });

  return (
    <PracticePageLayout
      icon={<BadgeOutlinedIcon />}
      kicker="Role-based"
      title="Practice questions matched to your target role."
      subtitle="Name the position you're preparing for. We'll surface ten interview questions you should expect to handle."
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5
        }}
      >
        <TextField
          id="job-position"
          label="Job position"
          variant="outlined"
          fullWidth
          sx={inputSx}
          value={jobPosition}
          onChange={e => setJobPosition(e.target.value)}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!jobPosition || loading}
            sx={primaryButtonSx}
          >
            {loading ? "Generating…" : "Generate questions"}
          </Button>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : questions.length > 0 ? (
        <Box sx={{ mt: 4 }}>{questionCards[currentQuestionIndex]}</Box>
      ) : null}
    </PracticePageLayout>
  );
};

export default Question;
